import {
  Box,
  Slider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Colors } from "Components/Layout/Themes/Colors";
import {
  CATEGORY_TO_PERCENTAGE_MAP,
  type CardTypeCategory,
} from "Components/Merchant/FeeCalculator/constants";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { useResponsiveValue } from "Hooks/useResponsiveValue";
import { Resources, useResource } from "Translations/Resources";

const COMPONENT_RESOURCES =
  Resources.Pages.Content.MerchantFeeCalculator.CardTypeRatioSlider;

type Props = {
  value: number;
  toggleValue: CardTypeCategory;
  onChange: (personalPercentage: number) => void;
  onToggleValueChange: (toggleValue: CardTypeCategory) => void;
};

export const CardTypeRatioSlider = ({
  value: personalPercentage,
  toggleValue,
  onChange,
  onToggleValueChange,
}: Props) => {
  const getResponsiveValue = useResponsiveValue();
  const { t } = useResource();

  const handleSliderChange = (
    _event: Event,
    newValue: number | Array<number>,
  ) => {
    if (typeof newValue === "number") {
      onChange(newValue);

      if (newValue < 30) {
        onToggleValueChange("mostly_commercial");
      } else if (newValue < 70) {
        onToggleValueChange("some_commercial");
      } else {
        onToggleValueChange("mostly_personal");
      }
    }
  };

  const commercialPercentage = 100 - personalPercentage;

  return (
    <PrimaryPaper>
      <Stack alignItems="center" gap={4}>
        <Typography variant="h5">
          {t(
            Resources.Pages.Content.MerchantFeeCalculator.CardTypeRatioSlider
              .Title,
          )}
        </Typography>
        <Box mb={1}>
          <ToggleButtonGroup
            color="primary"
            exclusive
            orientation={getResponsiveValue(["vertical", "horizontal"])}
            value={toggleValue}
            onChange={(_, value: CardTypeCategory) => {
              onToggleValueChange(value);
              onChange(value ? CATEGORY_TO_PERCENTAGE_MAP[value] : 50);
            }}
          >
            <ToggleButton value="mostly_personal">
              {t(COMPONENT_RESOURCES.ToggleButtons.MostlyPersonal)}
            </ToggleButton>
            <ToggleButton value="some_commercial">
              {t(COMPONENT_RESOURCES.ToggleButtons.SomeCommercial)}
            </ToggleButton>
            <ToggleButton value="mostly_commercial">
              {t(COMPONENT_RESOURCES.ToggleButtons.MostlyCommercial)}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box width="100%">
          <Slider
            value={personalPercentage}
            onChange={handleSliderChange}
            min={0}
            max={100}
            sx={{
              height: 40,
              "& .MuiSlider-track": {
                border: "none",
                borderRadius: "4px",
                backgroundColor: "#80B3FF",
              },
              "& .MuiSlider-rail": {
                opacity: 1,
                backgroundColor: Colors.PrimaryMain,
                borderRadius: "4px",
              },
              "& .MuiSlider-thumb": {
                height: 65,
                width: 7,
                borderRadius: "3px",
                backgroundColor: "white",
                boxShadow: "none",
              },
            }}
          />
          <Box display="flex" justifyContent="space-between" px={1} mb={2}>
            <Typography variant="body1">
              {`${t(COMPONENT_RESOURCES.CardTypeRatios.Personal)}: ${personalPercentage}%`}
            </Typography>
            <Typography variant="body1">
              {`${t(COMPONENT_RESOURCES.CardTypeRatios.Commercial)}: ${commercialPercentage}%`}
            </Typography>
          </Box>
        </Box>
      </Stack>
    </PrimaryPaper>
  );
};
